/**
 * Deck-shape analysis for the draft "coach" — a pure, derived read over a
 * DeckBuild that powers the cost-curve histogram and a single plain-language tip
 * shown under each seat's deck. It is read-only guidance: it never changes the
 * draft rules, the RNG, or what `draftToBuilds` returns, so it stays trivially
 * deterministic (a pure function of the deck, which is itself a pure function of
 * the seed). The aim is teaching — helping a newcomer feel a deck taking shape —
 * not enforcing a meta.
 */
import { cardTypeCost, type CardTypeId, type DeckBuild } from "./cards";
import { isAbilityId } from "./abilities";
import { pieceDef } from "./pieces";

/** Cost bands for the curve histogram: cheap (<=2), mid (3-5), heavy (6+). */
export type CostBand = "cheap" | "mid" | "heavy";

export function costBand(cost: number): CostBand {
  return cost <= 2 ? "cheap" : cost <= 5 ? "mid" : "heavy";
}

export interface DeckTip {
  readonly kind: "empty" | "thin" | "topheavy" | "good";
  readonly text: string;
}

export interface DeckShape {
  /** Total cards drafted. */
  readonly size: number;
  /** Total points spent. */
  readonly spent: number;
  /**
   * Deployable *fighters* — pieces that can actually move and contest the board.
   * Excludes immovable terrain (Hill/Valley/Ridge, value 0) and ability cards,
   * because those don't give you something to "play onto the board and fight."
   */
  readonly deployers: number;
  /** Card counts per cost band, in [cheap, mid, heavy] order, for the histogram. */
  readonly tiers: readonly [number, number, number];
  readonly tip: DeckTip;
}

/** Is this card a deployable fighter (movable, board-contesting piece)? */
function isFighter(id: CardTypeId): boolean {
  if (isAbilityId(id)) return false;
  const def = pieceDef(id);
  return def.deployable && !def.royal && def.value > 0;
}

/** Below this many fighters a deck risks running out of things to deploy. */
export const THIN_DEPLOYERS = 6;

function tipFor(size: number, deployers: number, tiers: readonly [number, number, number]): DeckTip {
  const [cheap, , heavy] = tiers;
  if (size === 0) {
    return { kind: "empty", text: "Pick cards to start building your army." };
  }
  if (deployers < THIN_DEPLOYERS) {
    return {
      kind: "thin",
      text: "Thin on pieces — you may run out of things to deploy. Add a few cheap pieces.",
    };
  }
  if (heavy > cheap) {
    return {
      kind: "topheavy",
      text: "Top-heavy — lots of big pieces but little to open with. Grab some cheap pieces.",
    };
  }
  return { kind: "good", text: "Nicely shaped — a healthy mix of cheap and strong." };
}

export function deckShape(build: DeckBuild): DeckShape {
  let size = 0;
  let spent = 0;
  let deployers = 0;
  const tiers: [number, number, number] = [0, 0, 0];
  for (const [card, count] of Object.entries(build) as [CardTypeId, number][]) {
    const n = count ?? 0;
    if (n <= 0) continue;
    size += n;
    spent += cardTypeCost(card) * n;
    if (isFighter(card)) deployers += n;
    const band = costBand(cardTypeCost(card));
    tiers[band === "cheap" ? 0 : band === "mid" ? 1 : 2] += n;
  }
  return { size, spent, deployers, tiers, tip: tipFor(size, deployers, tiers) };
}
